import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import type {
  Point,
  PointArray,
  PointSiArray,
} from './types/contour-definition-array-type';

import { LowRiskContourLabels } from '@src/config';
import { useActiveStudyStore } from '@src/store/active-study';

import { useUpdatedReportStore } from '@src/store/report-store';
import z from 'zod';

import { useAlert } from '@/src/composables/useAlert';

// Import Stores to reset state on contour changes
import { useAnalysisStore } from '@/src/store/analysis';

import { AnnotationArrowContour } from './annotation-arrow-contour';

import { AnnotationTextContour } from './annotation-text-contour';
import {
  ContourBase,
  ContourDefinitions,
  ContourId,
  ContourLabel,
  ContourLabelItemId,
  ContourProps,
  ContourSerializedDataSchema,
  ContourType,
  decodeContourLabelItemId,
  encodeContourLabelItemId,
} from './contour-definitions';
import { createLaxChordLines } from './contour-processing/createLaxChordLines';
import { createLaxExtendLine } from './contour-processing/createLaxExtendLine';
import { createLaxMyocardialPolygon } from './contour-processing/createLaxMyocardialFusionPolygon';
import { createLaxSegments } from './contour-processing/createLaxSegments';
import { createLaxTransmuralityPolygons } from './contour-processing/createLaxTransmuralityPolygons';
import { createSaxChordLines } from './contour-processing/createSaxChordLines';
import { createSaxMyocardialPolygon } from './contour-processing/createSaxMyocardialFusionPolygon';
import { createSaxOpenEpiMyocardialPolygon } from './contour-processing/createSaxOpenEpiMyocardialPolygon';
import { createSaxSegments } from './contour-processing/createSaxSegments';
import { createSaxTransmuralityPolygons } from './contour-processing/createSaxTransmuralityPolygons';

import { ContourRules } from './contour-rules';
import { LineContour } from './line-contour';
import { PointContour } from './point-contour';
import { PolygonClosedContour } from './polygon-closed-contour';
import { PolygonOpenContour } from './polygon-open-contour';

export const ContourSerializedDataByIdSchema = z.record(z.string(), ContourSerializedDataSchema);
export type ContourSerializedDataById = z.infer<typeof ContourSerializedDataByIdSchema>;

export const SerializedContoursSchema = z.record(z.string(), ContourSerializedDataByIdSchema);
export type SerializedContours = z.infer<typeof SerializedContoursSchema>;

export const SerializedContourContainerSchema = z.object({
  imageUID: z.string(),
  contours: SerializedContoursSchema,
});

export type SerializedContourContainer = z.infer<typeof SerializedContourContainerSchema>;

export class ContourContainer {
  // main key is label, sub key is id
  private _contours: Record<string, Record<string, ContourBase>> = {};
  private _imageUID: string = '';
  private _rules?: ContourRules;

  constructor(imageUID: string) {
    this._imageUID = imageUID;
  }

  // hierarchy rules (endo/epi/papillary/exclusion) enforced on user edits
  private get rules(): ContourRules {
    if (!this._rules) this._rules = new ContourRules(this);
    return this._rules;
  }

  // get available id string, '-1' if not available
  private getAvailableId(props: ContourProps) {
    // check if label is already in the container
    const labelContours = this._contours[props.label];
    if (!labelContours) {
      return encodeContourLabelItemId([0]);
    }

    const { multiplicity } = props;
    let availableId = encodeContourLabelItemId([-1]);
    for (let i = 0; i < multiplicity; i++) {
      const id = encodeContourLabelItemId([i]);
      if (!labelContours[id]) {
        availableId = id;
        break;
      }
    }
    return availableId;
  }

  public isPresentContourId(contourId: ContourId) {
    const labelContours = this._contours[contourId?.label];
    if (!labelContours) return false;
    return labelContours[contourId.id] !== undefined;
  }

  public isPresentContourLabel(label: ContourLabel) {
    const labelContours = this._contours[label];
    if (!labelContours) return false;
    return Object.keys(labelContours).length > 0;
  }

  private resetDependentContours(props: ContourProps) {
    const dependentContourLabels = props.dependendGeneratedContours;
    if (dependentContourLabels.length === 0) return;

    for (const dependentContourLabel of dependentContourLabels) {
      delete this._contours[dependentContourLabel];
    }
  }

  private resetAnalysisState() {
    const analysisStore = useAnalysisStore();
    analysisStore.resetStudy();

    const activeStudyStore = useActiveStudyStore();
    activeStudyStore.hasSavedState = false;

    const reportStore = useUpdatedReportStore();
    reportStore.resetStore();
  }

  private async confirmContourChange() {
    // Implementation for checking if the user can change the contour
    const alert = useAlert();
    // Default buttons (Cancel, Ignore, Continue)
    const userConfirm = await alert.confirm(
      'You are trying to modify a contour on an image that is used in the analysis. This will reset the analysis state. Do you want to continue?',
    );
    if (userConfirm) {
      this.resetAnalysisState();
      alert.info(
        'Analysis state has been reset. You can now modify the contour.',
      );
    }
  }

  private canUserChangeContour(label: ContourLabel): boolean {
    // First check whether this image is used in the analysis, if not, allow change without confirmation
    const analysisStore = useAnalysisStore();
    const biomarkerContainer = analysisStore.getBiomarkerContainer();
    const isImageUsedInAnalysis = biomarkerContainer.isImageUIDinAnalysis(
      this._imageUID,
    );
    if (!isImageUsedInAnalysis) return true;

    // Then check whether contour is risky type:
    if (LowRiskContourLabels.includes(label)) return true;
    // tv: should this wait for UI and return true if confirmed?
    void this.confirmContourChange();
    return false;
  }

  private handleCasesOpenContourAdd(probs: ContourProps) {
    if (probs.label === ContourLabel.LaxLvEndo) {
      const contourPolygon = this.getContour({
        label: probs.label,
        id: probs.id,
      })?.getPolygonScaled();
      if (!contourPolygon) {
        console.warn(
          'ContourContainer - handleCasesOpenContourAdd - LaxLvEndo not found',
        );
        return;
      }
      const startPoint = contourPolygon[0];
      const endPoint = contourPolygon[contourPolygon.length - 1];
      const basalPoint = {
        x: (startPoint.x + endPoint.x) / 2,
        y: (startPoint.y + endPoint.y) / 2,
      };
      console.log('basalPoint', basalPoint);

      const basalPointProps = {
        ...ContourDefinitions[ContourLabel.LaxLvEndoBasalPoint],
      };
      basalPointProps.imageUID = this._imageUID;
      basalPointProps.id = encodeContourLabelItemId([0]);
      this.addUserContourPolygon([basalPoint], basalPointProps);
    }
  }

  // add user editable contour, return true if state changed
  public addUserContourPolygon(
    polygon: PointArray,
    probs: ContourProps,
    replace: boolean = false,
    enforce: boolean = true,
  ) {
    // check for none
    if (probs.type === ContourType.None || probs.label === ContourLabel.None) {
      console.warn(
        'ContourContainer - addUserContourLine - ContourType or ContourLabel is not None',
      );
      return false;
    }

    // If the contour is low risk, we do not reset the analysis state
    const canChange = this.canUserChangeContour(probs.label);
    if (!canChange) return false;

    // check for given id or get available id, return if no id is available
    if (probs.id === encodeContourLabelItemId([-1])) {
      const id = this.getAvailableId(probs);
      if (id === encodeContourLabelItemId([-1])) {
        if (probs.multiplicity === 1) {
          // if multiplicity is 1, we modify

          probs.id = encodeContourLabelItemId([0]);
        }
        else {
          console.warn(
            'ContourContainer - addUserContourLine - No available id',
            probs.label,
          );
          return false;
        }
      }
      else {
        probs.id = id;
      }
    }

    // create contour id
    const contourId = {
      label: probs.label,
      id: probs.id,
    };

    // modify instead of create if present
    const modify = this.isPresentContourId(contourId) && !replace;

    // reject incompatible endo/epi additions (e.g. sax epi vs open epi, sax vs lax)
    if (enforce && !modify && !this.rules.canAddLabel(probs.label)) {
      return false;
    }

    let labelContours = this._contours[contourId.label];

    // handle by type
    if (probs.type === ContourType.PolygonClosedContour) {
      if (modify) {
        const contour = labelContours[contourId.id] as PolygonClosedContour;

        // clone: modifyContour mutates the point objects in place, so a plain
        // reference would be corrupted and restore a malformed contour
        const contourPolygon = contour.getPolygon().map((point: Point) => ({ ...point }));
        contour.modifyContour(polygon);
        if (
          contour.isValid()
          && (!enforce
            || this.rules.isEditAllowed(contourId, contour.getPolygonScaled()))
        ) {
          this.resetDependentContours(probs);
          if (enforce) this.rules.enforce(contourId);
          return true;
        }
        // restore polygon
        contour.setPolygon(contourPolygon);
        return false;
      }
      else {
        console.log('Saving without modifying contour', probs.label, probs.id);
        const newContour = new PolygonClosedContour(probs, polygon);
        if (!newContour.isValid()) return false;
        console.log('Contour Was Valid');

        if (
          enforce
          && !this.rules.isEditAllowed(contourId, newContour.getPolygonScaled())
        ) {
          return false;
        }

        if (!labelContours) {
          this._contours[contourId.label] = {};
          labelContours = this._contours[contourId.label];
        }
        labelContours[contourId.id] = newContour;
        this.resetDependentContours(probs);
        if (enforce) this.rules.enforce(contourId);
        return true;
      }
    }
    else if (probs.type === ContourType.PolygonOpenContour) {
      if (modify) {
        const contour = labelContours[contourId.id] as PolygonOpenContour;
        // clone: modifyContour mutates the point objects in place, so a plain
        // reference would be corrupted and restore a malformed contour
        const contourPolygon = contour.getPolygon().map((point: Point) => ({ ...point }));
        contour.modifyContour(polygon);
        if (
          contour.isValid()
          && (!enforce
            || this.rules.isEditAllowed(contourId, contour.getPolygonScaled()))
        ) {
          this.resetDependentContours(probs);
          if (enforce) this.rules.enforce(contourId);
          return true;
        }
        // restore polygon
        contour.setPolygon(contourPolygon);
        return false;
      }
      else {
        const newContour = new PolygonOpenContour(probs, polygon);
        if (!newContour.isValid()) return false;

        if (
          enforce
          && !this.rules.isEditAllowed(contourId, newContour.getPolygonScaled())
        ) {
          return false;
        }

        if (!labelContours) {
          this._contours[contourId.label] = {};
          labelContours = this._contours[contourId.label];
        }
        labelContours[contourId.id] = newContour;
        this.resetDependentContours(probs);
        this.handleCasesOpenContourAdd(probs);
        if (enforce) this.rules.enforce(contourId);
        return true;
      }
    }
    else if (probs.type === ContourType.LineContour) {
      const newContour = new LineContour(probs, polygon);
      if (!newContour.isValid()) return false;

      if (!labelContours) {
        this._contours[contourId.label] = {};
        labelContours = this._contours[contourId.label];
      }
      labelContours[contourId.id] = newContour;
      this.resetDependentContours(probs);
      return true;
    }
    else if (probs.type === ContourType.PointContour) {
      const newContour = new PointContour(probs, polygon);
      if (!newContour.isValid()) return false;

      if (!labelContours) {
        this._contours[contourId.label] = {};
        labelContours = this._contours[contourId.label];
      }
      labelContours[contourId.id] = newContour;
      this.resetDependentContours(probs);
      return true;
    }
    else if (probs.type === ContourType.AnnotationArrowContour) {
      const newContour = new AnnotationArrowContour(probs, polygon);
      if (!newContour.isValid()) return false;
      if (!labelContours) {
        this._contours[contourId.label] = {};
        labelContours = this._contours[contourId.label];
      }
      labelContours[contourId.id] = newContour;
      this.resetDependentContours(probs);
      return true;
    }
    else if (probs.type === ContourType.AnnotationTextContour) {
      const newContour = new AnnotationTextContour(probs, polygon);
      if (!newContour.isValid()) return false;

      if (!labelContours) {
        this._contours[contourId.label] = {};
        labelContours = this._contours[contourId.label];
      }
      labelContours[contourId.id] = newContour;
      this.resetDependentContours(probs);
      return true;
    }
    else {
      console.warn(
        'ContourContainer - addUserContourLine - Unknown contour type',
        probs.type,
      );
      return false;
    }
  }

  public translateContour(
    contourId: ContourId,
    distancePoint: [number, number],
    enforce: boolean = true,
  ) {
    if (
      !this._contours[contourId.label]
      || !this._contours[contourId.label][contourId.id]
    ) {
      console.warn(
        'ContourContainer - translateContour - Contour not found',
        contourId,
      );
      return false;
    }
    const canChange = this.canUserChangeContour(contourId.label);
    if (!canChange) return false;
    const contour = this._contours[contourId.label][contourId.id] as
      | PolygonClosedContour
      | PolygonOpenContour
      | LineContour
      | PointContour;

    // snapshot for restore if the translation is rejected
    const polygonBefore = contour.getPolygon().map((point: Point) => ({ ...point }));
    contour.translate(distancePoint);

    // reject a translated contour that violates the hierarchy (epi no longer
    // enclosing the endo, or a papillary / exclusion moved outside its endo)
    if (
      enforce
      && !this.rules.isEditAllowed(contourId, contour.getPolygonScaled())
    ) {
      contour.setPolygon(polygonBefore);
      return false;
    }

    this.resetDependentContours(contour.getProps());
    if (enforce) this.rules.enforce(contourId);

    return true;
  }

  public translateAllContours(distancePoint: [number, number]) {
    for (const label in this._contours) {
      for (const id in this._contours[label]) {
        // relative geometry is preserved, so hierarchy enforcement is skipped
        this.translateContour(
          { label: label as ContourLabel, id: id as ContourLabelItemId },
          distancePoint,
          false,
        );
      }
    }
    return true;
  }

  public deleteContour(contourId: ContourId) {
    if (
      !this._contours[contourId.label]
      || !this._contours[contourId.label][contourId.id]
    ) {
      // console.warn('ContourContainer - deleteContour - Contour not found', contourId);
      return false;
    }
    const canChange = this.canUserChangeContour(contourId.label);
    if (!canChange) return false;
    const props = this._contours[contourId.label][contourId.id].getProps();
    this.resetDependentContours(props);
    delete this._contours[contourId.label][contourId.id];
    // If the contour is low risk, we do not reset the analysis state

    return true;
  }

  public deleteAllContours() {
    for (const label in this._contours) {
      for (const id in this._contours[label]) {
        this.deleteContour({
          label: label as ContourLabel,
          id: id as ContourLabelItemId,
        });
      }
    }
    return true;
  }

  public deleteAllContoursOfLabel(label: ContourLabel) {
    if (this._contours[label]) {
      for (const id in this._contours[label]) {
        this.deleteContour({
          label,
          id: id as ContourLabelItemId,
        });
      }
    }
    return true;
  }

  public addGeneratedContour(props: ContourProps, contour: ContourBase) {
    const contourId = {
      label: props.label,
      id: props.id,
    };
    if (this.isPresentContourId(contourId)) {
      // console.warn('ContourContainer - addGeneratedContour - Contour already exists', contourId);
      return;
    }
    if (!this._contours[contourId.label]) {
      this._contours[contourId.label] = {};
    }
    this._contours[contourId.label][contourId.id] = contour;
    this.resetDependentContours(contour.getProps());
  }

  public getContour(id: ContourId): ContourBase | undefined {
    // If its null or undefined, return undefined
    if (!id) {
      return undefined;
    }
    if (this.isPresentContourId(id)) {
      return this._contours[id.label][id.id];
    }
    // SaxLvMyoFusion
    if (id.label === ContourLabel.SaxLvMyoFusion) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 2) {
        // console.warn('ContourContainer - id for SaxLvMyoFusion is not valid: ', labelIds);
        return undefined;
      }

      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];

      let endoId = {
        label: ContourLabel.SaxLvEndo,
        id: encodeContourLabelItemId([0]),
      };

      let epiId = {
        label: ContourLabel.SaxLvEpi,
        id: encodeContourLabelItemId([0]),
      };

      const openEpiId = {
        label: ContourLabel.SaxLvOpenEpi,
        id: encodeContourLabelItemId([0]),
      };

      if (endoOffset !== 0) {
        endoId = {
          label: ContourLabel.SaxLvTransmurality,
          id: encodeContourLabelItemId([endoOffset]),
        };
      }

      if (epiOffset !== 100) {
        epiId = {
          label: ContourLabel.SaxLvTransmurality,
          id: encodeContourLabelItemId([epiOffset]),
        };
      }

      const endoContour = this.getContour(endoId) as PolygonClosedContour;
      const epiContour = this.getContour(epiId) as PolygonClosedContour;
      const openEpiContour = this.getContour(openEpiId) as PolygonOpenContour;

      if (!endoContour || (!epiContour && !openEpiContour)) {
        // console.warn('ContourContainer - Missing endo or epi contour for SaxLvMyoFusion: ', endoId, epiId);
        return undefined;
      }

      let fusionPolygon: PointArray;
      if (epiContour) {
        fusionPolygon = createSaxMyocardialPolygon(endoContour, epiContour);
      }
      else {
        fusionPolygon = createSaxOpenEpiMyocardialPolygon(endoContour, openEpiContour);
      }

      const contourProps = {
        ...ContourDefinitions[ContourLabel.SaxLvMyoFusion],
      };
      contourProps.id = encodeContourLabelItemId([endoOffset, epiOffset]);
      contourProps.imageUID = this._imageUID;
      const fusionContour = new PolygonClosedContour(contourProps);
      fusionContour.setPolygon(fusionPolygon);

      this.addGeneratedContour(contourProps, fusionContour);
    }
    // SaxLvTransmurality
    if (id.label === ContourLabel.SaxLvTransmurality) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 1) {
        // console.warn('ContourContainer - id for SaxLvTransmurality is not valid: ', labelIds);
        return undefined;
      }

      const endoId = {
        label: ContourLabel.SaxLvEndo,
        id: encodeContourLabelItemId([0]),
      };

      const epiId = {
        label: ContourLabel.SaxLvEpi,
        id: encodeContourLabelItemId([0]),
      };

      const endoContour = this.getContour(endoId) as PolygonClosedContour;
      const epiContour = this.getContour(epiId) as PolygonClosedContour;

      if (!endoContour || !epiContour) {
        // console.warn('ContourContainer - Missing endo or epi contour for SaxLvTransmurality: ', endoId, epiId);
        return undefined;
      }

      const transmuralityPolygons = createSaxTransmuralityPolygons(
        endoContour,
        epiContour,
      );

      for (let i = 0; i <= 20; i++) {
        const contourProps = {
          ...ContourDefinitions[ContourLabel.SaxLvTransmurality],
        };
        contourProps.id = encodeContourLabelItemId([i * 5]);
        contourProps.imageUID = this._imageUID;
        const transmuralityContour = new PolygonClosedContour(contourProps);
        transmuralityContour.setPolygon(transmuralityPolygons[i]);
        this.addGeneratedContour(contourProps, transmuralityContour);
      }
    }
    // SaxLvChord
    if (id.label === ContourLabel.SaxLvChord) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 4) {
        // console.warn('ContourContainer - id for SaxLvChord is not valid: ', labelIds);
        return undefined;
      }
      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];
      const chordCount = labelIds[2];

      // let endoId = {
      //   label: ContourLabel.SaxLvEndo,
      //   id: encodeContourLabelItemId([0]),
      // };

      // let epiId = {
      //   label: ContourLabel.SaxLvEpi,
      //   id: encodeContourLabelItemId([0]),
      // };

      // if (endoOffset !== 0) {
      //   endoId = {
      //     label: ContourLabel.SaxLvTransmurality,
      //     id: encodeContourLabelItemId([endoOffset]),
      //   };
      // }

      // if (epiOffset !== 100) {
      //   epiId = {
      //     label: ContourLabel.SaxLvTransmurality,
      //     id: encodeContourLabelItemId([epiOffset]),
      //   };
      // }

      const endoId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([endoOffset]),
      };

      const epiId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([epiOffset]),
      };

      const insertionPointId = {
        label: ContourLabel.RvInsertion,
        id: encodeContourLabelItemId([0]),
      };

      const saxCenterlineId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([10]),
      };

      const endoContour = this.getContour(endoId) as PolygonClosedContour;
      const epiContour = this.getContour(epiId) as PolygonClosedContour;
      const insertionPoint = this.getContour(insertionPointId) as PointContour;
      const saxCenterlinePolygon = this.getContour(
        saxCenterlineId,
      ) as PolygonClosedContour;

      if (
        !endoContour
        || !epiContour
        || !insertionPoint
        || !saxCenterlinePolygon
      ) {
        // console.warn('ContourContainer - Missing endo or epi contour for SaxLvChord: ', endoId, epiId, insertionPointId);
        return undefined;
      }

      const saxChords = createSaxChordLines(
        endoContour,
        epiContour,
        insertionPoint,
        saxCenterlinePolygon,
        chordCount,
      );

      for (let i = 0; i < chordCount; i++) {
        const contourProps = { ...ContourDefinitions[ContourLabel.SaxLvChord] };
        contourProps.id = encodeContourLabelItemId([
          endoOffset,
          epiOffset,
          chordCount,
          i,
        ]);
        contourProps.imageUID = this._imageUID;
        const chordContour = new LineContour(contourProps);
        chordContour.setPolygon(saxChords[i]);
        this.addGeneratedContour(contourProps, chordContour);
      }
    }
    // SaxLvCenterChord
    if (id.label === ContourLabel.SaxLvCenterChord) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 4) {
        // console.warn('ContourContainer - id for SaxLvCenterChord is not valid: ', labelIds);
        return undefined;
      }
      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];
      const chordCount = labelIds[2];

      // let endoId = {
      //   label: ContourLabel.SaxLvEndo,
      //   id: encodeContourLabelItemId([0]),
      // };

      // let epiId = {
      //   label: ContourLabel.SaxLvEpi,
      //   id: encodeContourLabelItemId([0]),
      // };

      // if (endoOffset !== 0) {
      //   endoId = {
      //     label: ContourLabel.SaxLvTransmurality,
      //     id: encodeContourLabelItemId([endoOffset]),
      //   };
      // }

      // if (epiOffset !== 100) {
      //   epiId = {
      //     label: ContourLabel.SaxLvTransmurality,
      //     id: encodeContourLabelItemId([epiOffset]),
      //   };
      // }

      const endoId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([endoOffset]),
      };

      const epiId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([epiOffset]),
      };

      const insertionPointId = {
        label: ContourLabel.RvInsertion,
        id: encodeContourLabelItemId([0]),
      };

      const saxCenterlineId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([10]),
      };

      const endoContour = this.getContour(endoId) as PolygonClosedContour;
      const epiContour = this.getContour(epiId) as PolygonClosedContour;
      const insertionPoint = this.getContour(insertionPointId) as PointContour;
      const saxCenterlinePolygon = this.getContour(
        saxCenterlineId,
      ) as PolygonClosedContour;

      if (
        !endoContour
        || !epiContour
        || !insertionPoint
        || !saxCenterlinePolygon
      ) {
        // console.warn('ContourContainer - Missing endo or epi contour for SaxLvCenterChord: ', endoId, epiId, insertionPointId);
        return undefined;
      }

      const saxChords = createSaxChordLines(
        endoContour,
        epiContour,
        insertionPoint,
        saxCenterlinePolygon,
        chordCount,
        true,
      );

      for (let i = 0; i < chordCount; i++) {
        const contourProps = {
          ...ContourDefinitions[ContourLabel.SaxLvCenterChord],
        };
        contourProps.id = encodeContourLabelItemId([
          endoOffset,
          epiOffset,
          chordCount,
          i,
        ]);
        contourProps.imageUID = this._imageUID;
        const chordContour = new LineContour(contourProps);
        chordContour.setPolygon(saxChords[i]);
        this.addGeneratedContour(contourProps, chordContour);
      }
    }
    // SaxLvSegment
    if (id.label === ContourLabel.SaxLvSegment) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 4) {
        // console.warn('ContourContainer - id for SaxLvSegment is not valid: ', labelIds);
        return undefined;
      }
      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];
      const segmentCount = labelIds[2];

      // let endoId = {
      //   label: ContourLabel.SaxLvEndo,
      //   id: encodeContourLabelItemId([0]),
      // };

      // let epiId = {
      //   label: ContourLabel.SaxLvEpi,
      //   id: encodeContourLabelItemId([0]),
      // };

      // if (endoOffset !== 0) {
      //   endoId = {
      //     label: ContourLabel.SaxLvTransmurality,
      //     id: encodeContourLabelItemId([endoOffset]),
      //   };
      // }

      // if (epiOffset !== 100) {
      //   epiId = {
      //     label: ContourLabel.SaxLvTransmurality,
      //     id: encodeContourLabelItemId([epiOffset]),
      //   };
      // }

      const endoId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([endoOffset]),
      };

      const epiId = {
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([epiOffset]),
      };

      const endoContour = this.getContour(endoId) as PolygonClosedContour;
      const epiContour = this.getContour(epiId) as PolygonClosedContour;

      const saxChords: PointArray[] = [];
      for (let i = 0; i < segmentCount; i++) {
        const saxChordId = {
          label: ContourLabel.SaxLvChord,
          id: encodeContourLabelItemId([
            endoOffset,
            epiOffset,
            segmentCount,
            i,
          ]),
        };
        const chordContour = this.getContour(saxChordId) as
          | LineContour
          | undefined;
        if (chordContour) {
          saxChords.push(chordContour.getDensePolygon());
        }
      }

      if (!endoContour || !epiContour || saxChords.length !== segmentCount) {
        // console.warn('ContourContainer - Missing endo or epi contour or missing sax chords for SaxLvSegment: ', endoId, epiId, saxChords.length, segmentCount);
        return undefined;
      }

      const saxSegments = createSaxSegments(
        saxChords,
        endoContour.getPolygon(),
        epiContour.getPolygon(),
      );

      if (saxSegments.length !== segmentCount) {
        // console.warn('ContourContainer - Missing sax segments for SaxLvSegment: ', endoId, epiId, saxSegments.length, segmentCount);
        return undefined;
      }

      for (let i = 0; i < segmentCount; i++) {
        const contourProps = {
          ...ContourDefinitions[ContourLabel.SaxLvSegment],
        };
        contourProps.id = encodeContourLabelItemId([
          endoOffset,
          epiOffset,
          segmentCount,
          i,
        ]);
        contourProps.imageUID = this._imageUID;
        const segmentContour = new PolygonClosedContour(contourProps);
        segmentContour.setPolygon(saxSegments[i]);
        this.addGeneratedContour(contourProps, segmentContour);
      }
    }
    // LaxLvExtendEndo
    if (id.label === ContourLabel.LaxLvExtendEndo) {
      const endoId = {
        label: ContourLabel.LaxLvEndo,
        id: encodeContourLabelItemId([0]),
      };

      const endoContour = this.getContour(endoId) as PolygonOpenContour;
      if (!endoContour) {
        // console.warn('ContourContainer - Missing endo contour for LaxLvExtendEndo: ', endoId);
        return undefined;
      }

      const { extendLine } = createLaxExtendLine(endoContour);

      const contourProps = {
        ...ContourDefinitions[ContourLabel.LaxLvExtendEndo],
      };
      contourProps.id = encodeContourLabelItemId([0]);
      contourProps.imageUID = this._imageUID;
      const extendContour = new LineContour(contourProps);
      extendContour.setPolygon(extendLine);
      this.addGeneratedContour(contourProps, extendContour);
    }
    // LaxLvExtendEpi
    if (id.label === ContourLabel.LaxLvExtendEpi) {
      const epiId = {
        label: ContourLabel.LaxLvEpi,
        id: encodeContourLabelItemId([0]),
      };

      const epiContour = this.getContour(epiId) as PolygonOpenContour;
      if (!epiContour) {
        // console.warn('ContourContainer - Missing epi contour for LaxLvExtendEpi: ', epiId);
        return undefined;
      }

      const { extendLine } = createLaxExtendLine(epiContour);

      const contourProps = {
        ...ContourDefinitions[ContourLabel.LaxLvExtendEpi],
      };
      contourProps.id = encodeContourLabelItemId([0]);
      contourProps.imageUID = this._imageUID;
      const extendContour = new LineContour(contourProps);
      extendContour.setPolygon(extendLine);
      this.addGeneratedContour(contourProps, extendContour);
    }
    // LaxLvMyoFusion
    if (id.label === ContourLabel.LaxLvMyoFusion) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 2) {
        // console.warn('ContourContainer - id for LaxLvMyoFusion is not valid: ', labelIds);
        return undefined;
      }

      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];

      let endoId = {
        label: ContourLabel.LaxLvEndo,
        id: encodeContourLabelItemId([0]),
      };

      let epiId = {
        label: ContourLabel.LaxLvEpi,
        id: encodeContourLabelItemId([0]),
      };

      if (endoOffset !== 0) {
        endoId = {
          label: ContourLabel.LaxLvTransmurality,
          id: encodeContourLabelItemId([endoOffset]),
        };
      }

      if (epiOffset !== 100) {
        epiId = {
          label: ContourLabel.LaxLvTransmurality,
          id: encodeContourLabelItemId([epiOffset]),
        };
      }

      const endoContour = this.getContour(endoId) as PolygonOpenContour;
      const epiContour = this.getContour(epiId) as PolygonOpenContour;

      if (!endoContour || !epiContour) {
        // console.warn('ContourContainer - Missing endo or epi contour for LaxLvMyoFusion: ', endoId, epiId);
        return undefined;
      }

      const fusionPolygon = createLaxMyocardialPolygon(endoContour, epiContour);

      const contourProps = {
        ...ContourDefinitions[ContourLabel.LaxLvMyoFusion],
      };
      contourProps.id = encodeContourLabelItemId([endoOffset, epiOffset]);
      contourProps.imageUID = this._imageUID;
      const fusionContour = new PolygonClosedContour(contourProps);
      fusionContour.setPolygon(fusionPolygon);

      this.addGeneratedContour(contourProps, fusionContour);
    }
    // LaxLvTransmurality
    if (id.label === ContourLabel.LaxLvTransmurality) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 1) {
        // console.warn('ContourContainer - id for LaxLvTransmurality is not valid: ', labelIds);
        return undefined;
      }

      const endoId = {
        label: ContourLabel.LaxLvEndo,
        id: encodeContourLabelItemId([0]),
      };

      const epiId = {
        label: ContourLabel.LaxLvEpi,
        id: encodeContourLabelItemId([0]),
      };

      const endoContour = this.getContour(endoId) as PolygonOpenContour;
      const epiContour = this.getContour(epiId) as PolygonOpenContour;

      if (!endoContour || !epiContour) {
        // console.warn('ContourContainer - Missing endo or epi contour for LaxLvTransmurality: ', endoId, epiId);
        return undefined;
      }

      const transmuralityPolygons = createLaxTransmuralityPolygons(
        endoContour,
        epiContour,
      );

      for (let i = 0; i <= 20; i++) {
        const contourProps = {
          ...ContourDefinitions[ContourLabel.LaxLvTransmurality],
        };
        contourProps.id = encodeContourLabelItemId([i * 5]);
        contourProps.imageUID = this._imageUID;
        const transmuralityContour = new PolygonOpenContour(contourProps);
        transmuralityContour.setPolygon(transmuralityPolygons[i]);
        this.addGeneratedContour(contourProps, transmuralityContour);
      }
    }
    // LaxLvChord
    if (id.label === ContourLabel.LaxLvChord) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 4) {
        // console.warn('ContourContainer - id for LaxLvChord is not valid: ', labelIds);
        return undefined;
      }
      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];
      const chordCount = labelIds[2];

      // let endoId = {
      //   label: ContourLabel.LaxLvEndo,
      //   id: encodeContourLabelItemId([0]),
      // };

      // let epiId = {
      //   label: ContourLabel.LaxLvEpi,
      //   id: encodeContourLabelItemId([0]),
      // };

      // if (endoOffset !== 0) {
      //   endoId = {
      //     label: ContourLabel.LaxLvTransmurality,
      //     id: encodeContourLabelItemId([endoOffset]),
      //   };
      // }

      // if (epiOffset !== 100) {
      //   epiId = {
      //     label: ContourLabel.LaxLvTransmurality,
      //     id: encodeContourLabelItemId([epiOffset]),
      //   };
      // }

      const endoId = {
        label: ContourLabel.LaxLvTransmurality,
        id: encodeContourLabelItemId([endoOffset]),
      };

      const epiId = {
        label: ContourLabel.LaxLvTransmurality,
        id: encodeContourLabelItemId([epiOffset]),
      };

      const endoContour = this.getContour(endoId) as PolygonOpenContour;
      const epiContour = this.getContour(epiId) as PolygonOpenContour;

      if (!endoContour || !epiContour) {
        // console.warn('ContourContainer - Missing endo or epi contour for LaxLvChord: ', endoId, epiId);
        return undefined;
      }

      const laxChords = createLaxChordLines(
        endoContour,
        epiContour,
        chordCount,
      );

      for (let i = 0; i < chordCount; i++) {
        const contourProps = { ...ContourDefinitions[ContourLabel.LaxLvChord] };
        contourProps.id = encodeContourLabelItemId([
          endoOffset,
          epiOffset,
          chordCount,
          i,
        ]);
        contourProps.imageUID = this._imageUID;
        const chordContour = new LineContour(contourProps);
        chordContour.setPolygon(laxChords[i]);
        this.addGeneratedContour(contourProps, chordContour);
      }
    }
    // LaxLvCenterChord
    if (id.label === ContourLabel.LaxLvCenterChord) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 4) {
        // console.warn('ContourContainer - id for LaxLvCenterChord is not valid: ', labelIds);
        return undefined;
      }
      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];
      const chordCount = labelIds[2];

      // let endoId = {
      //   label: ContourLabel.LaxLvEndo,
      //   id: encodeContourLabelItemId([0]),
      // };

      // let epiId = {
      //   label: ContourLabel.LaxLvEpi,
      //   id: encodeContourLabelItemId([0]),
      // };

      // if (endoOffset !== 0) {
      //   endoId = {
      //     label: ContourLabel.LaxLvTransmurality,
      //     id: encodeContourLabelItemId([endoOffset]),
      //   };
      // }

      // if (epiOffset !== 100) {
      //   epiId = {
      //     label: ContourLabel.LaxLvTransmurality,
      //     id: encodeContourLabelItemId([epiOffset]),
      //   };
      // }

      const endoId = {
        label: ContourLabel.LaxLvTransmurality,
        id: encodeContourLabelItemId([endoOffset]),
      };

      const epiId = {
        label: ContourLabel.LaxLvTransmurality,
        id: encodeContourLabelItemId([epiOffset]),
      };

      const endoContour = this.getContour(endoId) as PolygonOpenContour;
      const epiContour = this.getContour(epiId) as PolygonOpenContour;

      if (!endoContour || !epiContour) {
        // console.warn('ContourContainer - Missing endo or epi contour for LaxLvCenterChord: ', endoId, epiId);
        return undefined;
      }

      const laxChords = createLaxChordLines(
        endoContour,
        epiContour,
        chordCount,
        true,
      );

      for (let i = 0; i < chordCount; i++) {
        const contourProps = {
          ...ContourDefinitions[ContourLabel.LaxLvCenterChord],
        };
        contourProps.id = encodeContourLabelItemId([
          endoOffset,
          epiOffset,
          chordCount,
          i,
        ]);
        contourProps.imageUID = this._imageUID;
        const chordContour = new LineContour(contourProps);
        chordContour.setPolygon(laxChords[i]);
        this.addGeneratedContour(contourProps, chordContour);
      }
    }
    // LaxLvSegment
    if (id.label === ContourLabel.LaxLvSegment) {
      const labelIds = decodeContourLabelItemId(id.id);
      if (labelIds.length !== 4) {
        // console.warn('ContourContainer - id for LaxLvSegment is not valid: ', labelIds);
        return undefined;
      }
      const endoOffset = labelIds[0];
      const epiOffset = labelIds[1];
      const segmentCount = labelIds[2];

      // let endoId = {
      //   label: ContourLabel.LaxLvEndo,
      //   id: encodeContourLabelItemId([0]),
      // };

      // let epiId = {
      //   label: ContourLabel.LaxLvEpi,
      //   id: encodeContourLabelItemId([0]),
      // };

      // if (endoOffset !== 0) {
      //   endoId = {
      //     label: ContourLabel.LaxLvTransmurality,
      //     id: encodeContourLabelItemId([endoOffset]),
      //   };
      // }

      // if (epiOffset !== 100) {
      //   epiId = {
      //     label: ContourLabel.LaxLvTransmurality,
      //     id: encodeContourLabelItemId([epiOffset]),
      //   };
      // }

      const endoId = {
        label: ContourLabel.LaxLvTransmurality,
        id: encodeContourLabelItemId([endoOffset]),
      };

      const epiId = {
        label: ContourLabel.LaxLvTransmurality,
        id: encodeContourLabelItemId([epiOffset]),
      };

      const endoContour = this.getContour(endoId) as PolygonOpenContour;
      const epiContour = this.getContour(epiId) as PolygonOpenContour;

      const chordCount = segmentCount - 1;
      if (chordCount < 1) {
        return undefined;
      }
      const laxChords: PointArray[] = [];
      for (let i = 0; i < chordCount; i++) {
        const laxChordId = {
          label: ContourLabel.LaxLvChord,
          id: encodeContourLabelItemId([endoOffset, epiOffset, chordCount, i]),
        };
        const chordContour = this.getContour(laxChordId) as
          | LineContour
          | undefined;
        if (chordContour) {
          laxChords.push(chordContour.getDensePolygon());
        }
      }

      if (!endoContour || !epiContour || laxChords.length !== chordCount) {
        // console.warn('ContourContainer - Missing endo or epi contour or missing lax chords for LaxLvSegment: ', endoId, epiId, laxChords.length, segmentCount);
        return undefined;
      }

      const laxSegments = createLaxSegments(
        laxChords,
        endoContour.getPolygon(),
        epiContour.getPolygon(),
      );

      if (laxSegments.length !== segmentCount) {
        // console.warn('ContourContainer - Missing lax segments for LaxLvSegment: ', endoId, epiId, laxSegments.length, segmentCount);
        return undefined;
      }

      for (let i = 0; i < segmentCount; i++) {
        const contourProps = {
          ...ContourDefinitions[ContourLabel.LaxLvSegment],
        };
        contourProps.id = encodeContourLabelItemId([
          endoOffset,
          epiOffset,
          segmentCount,
          i,
        ]);
        contourProps.imageUID = this._imageUID;
        const segmentContour = new PolygonClosedContour(contourProps);
        segmentContour.setPolygon(laxSegments[i]);
        this.addGeneratedContour(contourProps, segmentContour);
      }
    }

    if (this.isPresentContourId(id)) {
      return this._contours[id.label][id.id];
    }
    // console.warn('ContourContainer - getContour - Contour not available: ', id);
    return undefined;
  }

  public getContourPointArray(contourId: ContourId): PointArray {
    return this.getContour(contourId)?.getPolygonScaled() || [];
  }

  public getContourMaskCompleteIn(contourId: ContourId, imageData?: vtkImageData): PointSiArray {
    const con = this.getContour(contourId);
    return (con as PolygonClosedContour)?.getCompleteInImagePixels(imageData) || [];
  }

  public getSiStatistic(contourId: ContourId) {
    const contour = this.getContour(contourId) as PolygonClosedContour;
    return contour.getSignalIntensityStatistic();
  }

  public getDrawingContours(): Record<string, Record<string, ContourBase>> {
    return this._contours;
  }

  public serialize() {
    const serializedContours = {} as SerializedContours;

    for (const [label, contourMap] of Object.entries(this._contours)) {
      serializedContours[label] = {};
      const map: Record<string, any> = {};
      for (const [id, contour] of Object.entries(contourMap)) {
        if (contour.getProps().userEditable !== false) {
          map[id] = contour.serialize();
        }
      }
      if (Object.keys(map).length > 0) {
        serializedContours[label] = map;
      }
    }

    return {
      imageUID: this._imageUID,
      contours: serializedContours,
    };
  }

  public static deserialize(serialized: SerializedContourContainer): ContourContainer {
    const container = new ContourContainer(serialized.imageUID);

    for (const [label, contourData] of Object.entries(serialized.contours)) {
      for (const [, contourSerializedData] of Object.entries(
        contourData,
      )) {
        const serializedData = contourSerializedData;
        const { props } = serializedData;
        let contour;

        switch (props.type) {
          case ContourType.PolygonClosedContour:
            contour = new PolygonClosedContour(props);
            break;
          case ContourType.PolygonOpenContour:
            contour = new PolygonOpenContour(props);
            break;
          case ContourType.LineContour:
            contour = new LineContour(props);
            break;
          case ContourType.PointContour:
            contour = new PointContour(props);
            break;
          case ContourType.None:
          case ContourType.AnnotationArrowContour:
          case ContourType.AnnotationTextContour:
          default:
            continue;
        }

        contour.setPolygon(serializedData.polygon);
        if (!container._contours[label]) {
          container._contours[label] = {};
        }
        container._contours[label][props.id] = contour;
      }
    }
    return container;
  }
}
